import React, { useCallback, useEffect, useState } from "react";
import { CareRecipient } from "../../../../../domain/care_recipient/CareRecipient";
import { CareRecipientId } from "../../../../../domain/care_recipient/CareRecipientId";
import { CareRecipientWithDataSyncInformation } from "../../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { DateObject } from "../../../../../domain/fundamental/DateObject";
import { Paths } from "../../../../../Paths";
import { useAppDispatch } from "../../../../../state/store";
import { deleteCareRecipientAction, updatePeriodAction } from "../../../../../state/usecase/admin/care_recipient/actions";
import { selectCareRecipientAction } from "../../../../../state/usecase/care_recipient/actions";
import { useSearchedCareRecipientList } from "../../../../hooks/admin/care_recipient/useSearchedCareRecipientList";
import { Loading } from "../../../common/Loading";
import { NoData } from "../../../common/no_data/NoData";
import { DateSelector } from "./DateSelector";
import { EditIcon } from "../../../common/icons/EditIcon";
import { RemoveIcon } from "../../../common/icons/RemoveIcon";
import { GraphIcon } from "../../../common/icons/GraphIcon";
import { HukidashiIcon } from "../../../common/icons/HukidashiIcon";
import { TableMenuButton } from "./TableMenuButton";
import { Button } from "../../../common/button/Button";
import { useNavigate } from "react-router-dom";

export const CareRecipientsTableBody = React.memo(function CareRecipientsTableBody() {
    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const careRecipientList = useSearchedCareRecipientList();

    const saveCalendarDate = useCallback((careRecipientId: CareRecipientId) => (startDate: DateObject, endDate: DateObject) => {
        dispatch(updatePeriodAction({
            careRecipientId: careRecipientId.value,
            startDate: startDate.format(),
            endDate: endDate.format()
        }));
    }, [dispatch]);

    const handleMoveToDashBoardClicked = useCallback((careRecipient: CareRecipient) => () => {
        dispatch(selectCareRecipientAction({ id: careRecipient.id, name: careRecipient.name, houseId: careRecipient.houseId, branchCode: careRecipient.branchCode, customerCode: careRecipient.customerCode }));
        navigate(`${Paths.dashBoard}/${careRecipient.id.value}`);
        window.scrollTo(0, 0);
    }, [dispatch, navigate]);

    const handleMoveToEditClicked = useCallback((careRecipientId: CareRecipientId) => () => navigate(`${Paths.admin}${Paths.careRecipient}${Paths.edit}/${careRecipientId.value}`), [navigate]);

    const handleDeleteClicked = useCallback((careRecipient: CareRecipient) => () => {
        if (!window.confirm(`${careRecipient.name.value}さんを削除しますか？`)) return;

        dispatch(
            deleteCareRecipientAction({
                careRecipientId: careRecipient.id
            })
        );
    }, [dispatch]);

    const handleCareCommentButtonClicked = useCallback((careRecipientId: CareRecipientId) => () => navigate(`${Paths.admin}${Paths.careComment}${Paths.add}/${careRecipientId.value}`), [navigate]);

    const handleTestDataRegisterButtonClicked = useCallback((careRecipientId: CareRecipientId) => () => navigate(`/test_data/estimated_behavior/${careRecipientId.value}`), [navigate]);

    return (
        <tbody>
            {/* レスポンス取得中はローディングを表示 */}
            {!careRecipientList && (
                <tr>
                    <td colSpan={4}>
                        <Loading />
                    </td>
                </tr>
            )}
            {/* レスポンス取得後データがない場合はデータ無しの表示 */}
            {!careRecipientList?.length && (
                <tr>
                    <td colSpan={4}>
                        <NoData />
                    </td>
                </tr>
            )}
            {/* データがある場合の表示 */}
            {careRecipientList?.map(careRecipientInformation => (
                <TableRow
                    key={careRecipientInformation.careRecipient.id.value}
                    careRecipientInformation={careRecipientInformation}
                    saveCalendarDate={saveCalendarDate}
                    handleMoveToEditClicked={handleMoveToEditClicked}
                    handleMoveToDashBoardClicked={handleMoveToDashBoardClicked}
                    handleDeleteClicked={handleDeleteClicked}
                    handleCareCommentButtonClicked={handleCareCommentButtonClicked}
                    handleTestDataRegisterButtonClicked={handleTestDataRegisterButtonClicked}
                />
            ))}
        </tbody>
    );
});

const TableRow = React.memo(function TableRow(props: TableRowProps) {
    const {
        careRecipientInformation,
        saveCalendarDate,
        handleMoveToEditClicked,
        handleMoveToDashBoardClicked,
        handleDeleteClicked,
        handleCareCommentButtonClicked,
        handleTestDataRegisterButtonClicked
    } = props;

    const [alertPeriodStart, setAlertPeriodStart] = useState(careRecipientInformation.alertPeriod?.start ?? null);
    
    const [alertPeriodEnd, setAlertPeriodEnd] = useState(careRecipientInformation.alertPeriod?.end ?? null);

    useEffect(() => {
        // アラート一覧の方から更新される場合があるので、careRecipientInformationの変更を検知したらsetStateする
        setAlertPeriodStart(careRecipientInformation.alertPeriod?.start ?? null);
        setAlertPeriodEnd(careRecipientInformation.alertPeriod?.end ?? null);
    }, [careRecipientInformation]);

    return (
        <tr className={"recipient"}>
            <td>
                {careRecipientInformation.careRecipient.houseId?.value}
                {process.env.REACT_APP_TEST_PAGE_DISPLAY_FLG === "1" && (
                    <Button
                        colorReversed
                        onClick={handleTestDataRegisterButtonClicked(careRecipientInformation.careRecipient.id)}
                        label={"テストデータ登録"}
                        height={30}
                        width={200}
                    />
                )}
            </td>
            <td>
                {careRecipientInformation.careRecipient.name.value}
            </td>
            <td>
                <div>{careRecipientInformation.dataSyncStartTime?.date.format()}</div>
                <div className="date_selector">
                    <DateSelector
                        defaultStartDate={alertPeriodStart}
                        defaultEndDate={alertPeriodEnd}
                        handleSaveClicked={saveCalendarDate(careRecipientInformation.careRecipient.id)}
                        dataSyncStartDate={careRecipientInformation.dataSyncStartTime?.date}
                    />
                </div>
                <div>{careRecipientInformation.dataSyncEndTime?.date.format() || "-"}</div>
            </td>
            <td>
                <div className={"menu_buttons"}>
                    <TableMenuButton
                        onClick={handleMoveToEditClicked(careRecipientInformation.careRecipient.id)}
                        icon={<EditIcon />}
                        label={"編集"}
                    />
                    <TableMenuButton
                        onClick={handleMoveToDashBoardClicked(careRecipientInformation.careRecipient)}
                        icon={<GraphIcon />}
                        label={"ダッシュボード"}
                    />
                    <TableMenuButton
                        onClick={handleCareCommentButtonClicked(careRecipientInformation.careRecipient.id)}
                        icon={<HukidashiIcon />}
                        label={"アドバイスコメント"}
                    />
                    <TableMenuButton
                        className={"remove"}
                        onClick={handleDeleteClicked(careRecipientInformation.careRecipient)}
                        icon={<RemoveIcon />}
                        label={"削除"}
                    />
                </div>
            </td>
        </tr>
    );
});

type TableRowProps = {
    careRecipientInformation: CareRecipientWithDataSyncInformation,
    saveCalendarDate: (careRecipientId: CareRecipientId) => (startDate: DateObject, endDate: DateObject) => void,
    handleMoveToEditClicked: (careRecipientId: CareRecipientId) => () => void,
    handleMoveToDashBoardClicked: (careRecipient: CareRecipient) => () => void,
    handleDeleteClicked: (careRecipient: CareRecipient) => () => void,
    handleCareCommentButtonClicked: (careRecipientId: CareRecipientId) => () => void,
    handleTestDataRegisterButtonClicked: (careRecipientId: CareRecipientId) => () => void
}
